import { rufe } from './client';
import type { Benutzer, Geraet, Recht, Sitzung } from './anmeldung';

/**
 * Die Benutzerverwaltung über HTTP (Kapitel 15).
 *
 * Zwei Rechte teilen die Endpunkte: `einstellungen/sehen` für alles Lesende,
 * `einstellungen/konfigurieren` für alles Schreibende. Ein eigenes Objekt für
 * Benutzer gibt es im Rechtemodell nicht (BEN-02 nennt sechs Objekte, und
 * eines zu erfinden wäre eine Änderung ohne Auftrag).
 */

export type BenutzerZeile = Benutzer & {
  readonly position: string | null;
  /**
   * RES-28: die Ressource hinter einem Dienstleisterkonto, ausgeschrieben.
   *
   * Die Kennung steckt in `ressource` und sagt niemandem, wessen Zugang er
   * gerade sperrt. `null` bei jedem anderen Konto.
   */
  readonly ressourceName: string | null;
  /** Die Kennungen der Rollen — zum Prüfen, nicht zum Anzeigen. */
  readonly rollen: readonly string[];
  /** Die Bezeichnungen derselben Rollen, nach Bezeichnung sortiert (Auftrag CRM-79). */
  readonly rollenBezeichnungen: readonly string[];
  /** UTC nach ISO 8601; die Anzeige rechnet in Ortszeit um (LOG-03). */
  readonly letzteAnmeldung: string | null;
  readonly zweiterFaktorEingerichtet: boolean;
  /**
   * Hat das Konto ein Kennwort? (BEN-21)
   *
   * Ohne diese Auskunft sieht ein Konto ohne Anmeldedaten genauso aus wie eines
   * mit einem ungenutzten Kennwort — `letzteAnmeldung` ist in beiden Fällen
   * leer. Die zweiundvierzig Trainerzugänge vom 12.09.2026 sind genau dieser
   * Fall: angelegt, mit Rolle, ohne Kennwort.
   */
  readonly kennwortGesetzt: boolean;
};

export type Rolle = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly ausgeliefert: boolean;
};

export type Position = {
  readonly kennung: string;
  readonly bezeichnung: string;
};

/**
 * BEN-17: was jemand darf und **woher** es kommt.
 *
 * `Recht` trägt die Herkunft bereits (`ausRollen`, `ueberVertretung`) — das
 * ist genau die Auskunft, die BEN-17 verlangt, und sie muss dieselbe sein wie
 * die, mit der die Oberfläche des Benutzers arbeitet.
 */
export type Berechtigung = Recht;

/** BEN-09: ein Vier-Augen-Vorgang. */
export type Bestaetigung = {
  readonly id: string;
  readonly gegenstand: string;
  /** Der Gegenstand zum Lesen — bei einer Rolle ihre Bezeichnung. */
  readonly gegenstandName: string;
  readonly handlung: 'zuweisen' | 'entziehen';
  readonly betroffener: string;
  readonly betroffenerName: string;
  readonly grund: string;
  readonly beantragtVon: string;
  readonly beantragtVonName: string;
  readonly beantragtAm: string;
  readonly bestaetigtVon: string | null;
  readonly bestaetigtAm: string | null;
  readonly abgelehntAm: string | null;
  readonly verbrauchtAm: string | null;
};

/* ── Lesen ───────────────────────────────────────────────────────────────── */

export async function liste(filter: {
  readonly suche?: string;
  readonly status?: Benutzer['status'];
  readonly team?: string;
} = {}): Promise<readonly BenutzerZeile[]> {
  const frage = new URLSearchParams();
  if (filter.suche !== undefined && filter.suche !== '') frage.set('suche', filter.suche);
  if (filter.status !== undefined) frage.set('status', filter.status);
  if (filter.team !== undefined && filter.team !== '') frage.set('team', filter.team);
  const anhang = frage.toString() === '' ? '' : `?${frage.toString()}`;
  const antwort = await rufe<{ benutzer: readonly BenutzerZeile[] }>(`/verwaltung/benutzer${anhang}`);
  return antwort.benutzer;
}

export async function einzeln(id: string, signal?: AbortSignal): Promise<BenutzerZeile> {
  return rufe(`/verwaltung/benutzer/${id}`, signal === undefined ? {} : { signal });
}

export async function berechtigungen(id: string, signal?: AbortSignal): Promise<readonly Berechtigung[]> {
  const antwort = await rufe<{ rechte: readonly Berechtigung[] }>(
    `/verwaltung/benutzer/${id}/berechtigungen`,
    signal === undefined ? {} : { signal },
  );
  return antwort.rechte;
}

export async function rollen(signal?: AbortSignal): Promise<readonly Rolle[]> {
  const antwort = await rufe<{ rollen: readonly Rolle[] }>('/verwaltung/rollen', signal === undefined ? {} : { signal });
  return antwort.rollen;
}

export async function positionen(): Promise<readonly Position[]> {
  const antwort = await rufe<{ positionen: readonly Position[] }>('/verwaltung/positionen');
  return antwort.positionen;
}

/* ── Schreiben ───────────────────────────────────────────────────────────── */

export async function anlegen(angabe: {
  readonly anmeldename: string;
  readonly name: string;
  readonly email: string;
  /*
   * HIER GIBT ES KEIN DIENSTLEISTERKONTO.
   *
   * Es braucht eine Ressource (RES-28), und die steht in der
   * Benutzerverwaltung nicht zur Verfügung — ein Auswahlfeld über mehrere
   * hundert Trainer wäre die Stelle, an der sich der falsche auswählen lässt.
   * Der Onlinezugang wird deshalb an der RESSOURCE eingerichtet.
   */
  readonly kontoart: 'person' | 'technisch';
  readonly team: string | null;
  readonly sichtbarkeit: 'eigene' | 'team' | 'alle';
  readonly position: string | null;
}): Promise<{ readonly id: string; readonly anfangskennwort: string | null }> {
  return rufe('/verwaltung/benutzer', { verfahren: 'POST', rumpf: angabe });
}

export async function statusSetzen(
  id: string,
  status: Benutzer['status'],
  nachfolger: string | null = null,
): Promise<void> {
  await rufe<void>(`/verwaltung/benutzer/${id}/status`, {
    verfahren: 'POST',
    rumpf: { status, nachfolger },
  });
}

export async function rolleZuweisen(id: string, rolle: string): Promise<void> {
  await rufe<void>(`/verwaltung/benutzer/${id}/rollen/${encodeURIComponent(rolle)}`, {
    verfahren: 'POST',
  });
}

export async function rolleEntziehen(id: string, rolle: string): Promise<void> {
  await rufe<void>(`/verwaltung/benutzer/${id}/rollen/${encodeURIComponent(rolle)}`, {
    verfahren: 'DELETE',
  });
}

/**
 * BEN-06: Kennwort zurücksetzen.
 *
 * Das Anfangskennwort steht **einmal** in der Antwort und nirgends sonst —
 * gespeichert wird nur sein Hash. Wer es verliert, setzt erneut zurück.
 */
export async function passwortZuruecksetzen(
  id: string,
): Promise<{ readonly anfangskennwort: string }> {
  return rufe(`/verwaltung/benutzer/${id}/kennwort-zuruecksetzen`, { verfahren: 'POST' });
}

/** SYS-05: den zweiten Faktor zurücksetzen — der Benutzer richtet ihn neu ein. */
export async function zweitenFaktorZuruecksetzen(id: string): Promise<void> {
  await rufe<void>(`/verwaltung/benutzer/${id}/zweiter-faktor-zuruecksetzen`, {
    verfahren: 'POST',
  });
}

/** AA-003: neue Notfallcodes. Die alten verfallen dabei. */
export async function notfallcodes(id: string): Promise<{ readonly codes: readonly string[] }> {
  return rufe(`/verwaltung/benutzer/${id}/notfallcodes`, { verfahren: 'POST' });
}

/* ── Sitzungen und Geräte (BEN-07, SYS-05) ───────────────────────────────── */

export async function sitzungen(id: string, signal?: AbortSignal): Promise<readonly Sitzung[]> {
  return rufe(`/verwaltung/benutzer/${id}/sitzungen`, signal === undefined ? {} : { signal });
}

export async function fernAbmelden(
  id: string,
  sitzung?: string,
): Promise<{ readonly beendet: number }> {
  const anhang = sitzung === undefined ? '' : `?sitzung=${sitzung}`;
  return rufe(`/verwaltung/benutzer/${id}/sitzungen${anhang}`, { verfahren: 'DELETE' });
}

export async function geraete(id: string, signal?: AbortSignal): Promise<readonly Geraet[]> {
  return rufe(`/verwaltung/benutzer/${id}/geraete`, signal === undefined ? {} : { signal });
}

export async function geraetWiderrufen(id: string, geraet: string): Promise<void> {
  await rufe<void>(`/verwaltung/benutzer/${id}/geraete/${geraet}`, { verfahren: 'DELETE' });
}

/* ── Vier-Augen-Vorgänge (BEN-09) ────────────────────────────────────────── */

export async function bestaetigungen(nurOffene = false): Promise<readonly Bestaetigung[]> {
  const antwort = await rufe<{ vorgaenge: readonly Bestaetigung[] }>(
    `/verwaltung/bestaetigungen${nurOffene ? '?nur=offene' : ''}`,
  );
  return antwort.vorgaenge;
}

export async function bestaetigungBeantragen(angabe: {
  readonly handlung: 'zuweisen' | 'entziehen';
  readonly betroffener: string;
  readonly grund: string;
}): Promise<{ readonly id: string }> {
  return rufe('/verwaltung/bestaetigungen', { verfahren: 'POST', rumpf: angabe });
}

/**
 * Der zweite Administrator entscheidet.
 *
 * Dass es nicht derselbe sein darf, prüft der Server — hier steht nur der
 * Aufruf. Eine Prüfung in der Maske wäre eine zweite Wahrheit und liesse sich
 * ohnehin umgehen.
 */
export async function bestaetigungEntscheiden(
  id: string,
  entscheidung: 'bestaetigen' | 'ablehnen',
): Promise<void> {
  await rufe<void>(`/verwaltung/bestaetigungen/${id}/${entscheidung}`, { verfahren: 'POST' });
}

/**
 * Wie sich dieses Haus nennt (0254).
 *
 * Kopfzeile und Fenstertitel kommen aus `sys.einstellung` und nicht mehr aus
 * dem Quelltext: Mit mehreren Mandanten stuende sonst in jeder Instanz der
 * Name der ersten — und im Browserreiter dreimal derselbe Titel, obwohl
 * Produktion, Staging und ein zweiter Kunde nebeneinander offen sind.
 *
 * Kein eigenes Recht: Der Kopf steht auf jeder Maske und gilt jedem
 * Angemeldeten.
 */
export type Oberflaeche = {
  readonly haus?: string;
  readonly anwendung?: string;
  readonly fenstertitel?: string;
  /**
   * `text` oder `logo` (0255). Was oben links steht: die beiden Zeilen aus
   * `haus` und `anwendung` — oder das Produktzeichen an ihrer Stelle.
   *
   * Absichtlich `string` und keine Aufzaehlung: Der Wert kommt aus der
   * Datenbank, und eine aeltere Oberflaeche darf an einem Wert, den sie noch
   * nicht kennt, nicht zerbrechen. Sie zeigt dann den Text.
   */
  readonly marke?: string;
};

export async function oberflaeche(): Promise<Oberflaeche> {
  return rufe<Oberflaeche>('/verwaltung/oberflaeche');
}

/**
 * Eine Einstellung des Hauses (TAG-25, Tabelle 10).
 *
 * Der Schlüssel ist die Adresse im Code, die Beschreibung die einzige
 * Erklärung, die der Anwender bekommt — sie kommt deshalb mit dem Wert.
 * Die Bezeichnung ist der Name in der Maske (0294); `null`, wenn die
 * Migration des Schlüssels keinen mitgebracht hat — dann steht dort der
 * Schlüssel.
 */
export type Einstellung = {
  readonly schluessel: string;
  readonly bezeichnung: string | null;
  readonly wert: string;
  readonly beschreibung: string | null;
  readonly geaendertAm: string;
};

export async function einstellungen(): Promise<readonly Einstellung[]> {
  const antwort = await rufe<{ readonly einstellungen: readonly Einstellung[] }>(
    '/verwaltung/einstellungen',
  );
  return antwort.einstellungen;
}

export async function einstellungSetzen(
  schluessel: string,
  wert: string,
): Promise<{ readonly gesetzt: true }> {
  return rufe(`/verwaltung/einstellungen/${encodeURIComponent(schluessel)}`, {
    verfahren: 'PATCH',
    rumpf: { wert },
  });
}

/**
 * Die Anmelderichtlinie (BEN-06, BEN-07, SYS-05; AeA-48). In der Verwaltung
 * lesend — bearbeitbar ist nur der Kennwortablauf.
 */
export type Anmelderichtlinie = {
  readonly mindestlaenge: number;
  readonly historieGenerationen: number;
  readonly fehlversucheBisSperre: number;
  readonly sperrdauerMinuten: number;
  readonly sitzungLeerlaufMinuten: number;
  readonly sitzungHoechstdauerStunden: number;
  readonly zweiFaktorPflicht: boolean;
  readonly notfallcodesJeBenutzer: number;
  readonly geraetGueltigTage: number;
  /** `null`: Kennwörter laufen nicht ab (Vorgabe). */
  readonly kennwortAblaufTage: number | null;
};

/** AeA-48: die Auswahl „Kennwörter laufen ab“. */
export type KennwortAblaufWahl = null | 90 | 180 | 365;

export async function anmelderichtlinie(): Promise<Anmelderichtlinie> {
  return rufe<Anmelderichtlinie>('/verwaltung/anmelderichtlinie');
}

export async function kennwortAblaufSetzen(
  tage: KennwortAblaufWahl,
): Promise<{ readonly alt: number | null; readonly neu: number | null }> {
  return rufe('/verwaltung/anmelderichtlinie/kennwortablauf', {
    verfahren: 'PATCH',
    rumpf: { tage },
  });
}
